import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { SpacerView } from './SpacerView';

export const SPACER_SIZES = ['sm', 'md', 'lg'] as const;
export type SpacerSize = (typeof SPACER_SIZES)[number];

export const DEFAULT_SPACER_SIZE: SpacerSize = 'md';

export const SPACER_HEIGHT_PX: Record<SpacerSize, number> = { sm: 8, md: 20, lg: 40 };

export function normalizeSpacerSize(value: unknown): SpacerSize {
    return (SPACER_SIZES as readonly string[]).includes(value as string)
        ? (value as SpacerSize)
        : DEFAULT_SPACER_SIZE;
}

/**
 * Deliberate vertical space, so authors stop faking gaps with empty paragraphs.
 * Registered in the schema unconditionally (see Callout for why); only the
 * toolbar affordance is gated, via RichEditorProps.allowLayoutBlocks.
 */
export const Spacer = Node.create({
    name: 'spacer',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: false,

    addAttributes() {
        return {
            size: {
                default: DEFAULT_SPACER_SIZE,
                parseHTML: (element) => normalizeSpacerSize(element.getAttribute('data-size')),
                renderHTML: (attributes) => ({
                    'data-size': normalizeSpacerSize((attributes as { size?: unknown }).size)
                })
            }
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-spacer]' }];
    },

    renderHTML({ HTMLAttributes, node }) {
        const size = normalizeSpacerSize(node.attrs.size);
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-spacer': '',
                style: `height:${SPACER_HEIGHT_PX[size]}px`
            })
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(SpacerView);
    }
});
